import { StateCreator } from 'zustand';
import {
  BinaryOptionsType,
  CycleHangarsEstatesFeatureAttributes,
  CycleHangarsFeatureAttributes,
} from '@types';
import { PartitionFileObjectsType } from '@utils';

export interface cycleHangarIssueOptions {
  title: string;
  description?: string;
}

export type CaseIDProps = {
  case_reference: string;
  sla_text: string;
};

export type DateFieldType = string | number;

export type SelectedDate = {
  selectedYear: DateFieldType;
  selectedMonth: DateFieldType;
  selectedDay: DateFieldType;
};

export type PhotoCoordinatesType = {
  latitude: number;
  longitude: number;
} | null;

const clearSelectedDate = {
  selectedDay: '',
  selectedMonth: '',
  selectedYear: '',
};

export interface ServiceLocationDetailsObjectType {
  address_line_1: string;
  address_line_2: string;
  country: string;
  county: string;
  easting: string | null;
  latitude: number | null;
  longitude: number | null;
  northing: string | null;
  postcode: string;
  town_city: string;
  UPRN: string;
  USRN: string;
  full_address?: string;
}
export interface CycleHangarIdSlice {
  hangar_id: string;
}

export type cycleHangarIssueTypes =
  | 'BikeTheft'
  | 'BrokenLock'
  | 'DamagedOrVandalism'
  | 'Cleaning'
  | 'AbandonedOrUnauthorisedBike'
  | 'SomethingElse'
  | null;

// This is a placeholder the states will be updated during development.
export interface IssueDetailsSlice {
  cycleHangarIssue: cycleHangarIssueTypes;
  serviceAssetDetails:
    | CycleHangarsFeatureAttributes
    | CycleHangarsEstatesFeatureAttributes
    | null;
  updateServiceAssetDetails: (
    serviceAssetDetails:
      | CycleHangarsFeatureAttributes
      | CycleHangarsEstatesFeatureAttributes
      | null,
  ) => void;
  updateCycleHangarIssue: (cycleHangarIssue: cycleHangarIssueTypes) => void;
  cycleHangarIssueOptions: cycleHangarIssueOptions[] | null;
  isSecured: string | null;
  updateIsSecured: (isSecured: string | null) => void;
  doesCodeMatch: BinaryOptionsType;
  doesKeyTurn: BinaryOptionsType;
  updateDoesCodeMatch: (doesCodeMatch: BinaryOptionsType) => void;
  updateDoesKeyTurn: (doesKeyTurn: BinaryOptionsType) => void;
  confirmation: CaseIDProps | undefined;
  updateConfirmation: (case_id: CaseIDProps | undefined) => void;
  receiveConfirmationEmail: string;
  updateReceiveConfirmationEmail: (receiveConfirmationEmail: string) => void;
  caseReferenceId: string;
  slaText: string;
  updateCaseReferenceId: (caseReferenceId: string) => void;
  updateSlaText: (slaText: string | undefined) => void;
  wantConfirmationEmail: BinaryOptionsType;
  resetIssueDetails: () => void;
  resetIssueDetailsInput: () => void;
  resetConfirmationInformation: () => void;
  uploadedImages: string[];
  uploadedImagesFileName: string[];
  uploadedImagesFileType: string[];
  uploadedImagesBlob: {
    blob_uri: string;
    blob_endpoint: string;
    filename: string;
  }[];
  fileDetailsObjects: { name: string; path: string }[];
  serviceLocationDetails: ServiceLocationDetailsObjectType;
  updateServiceLocationDetails: (
    serviceLocationDetails: ServiceLocationDetailsObjectType,
  ) => void;
  updateUploadedImages: (uploadedImages: PartitionFileObjectsType) => void;
  updateUploadedImagesBlobs: (
    uploadedImagesBlob: {
      blob_uri: string;
      blob_endpoint: string;
      filename: string;
    }[],
  ) => void;
  isPhotoLocationStored: boolean;
  updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) => void;
  photoCoordinates: PhotoCoordinatesType;
  updatePhotoCoordinates: (photoCoordinates: PhotoCoordinatesType) => void;
  fileWithLoadedCoordinates: string;
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) => void;
  knowsCycleHangarId: BinaryOptionsType;
  updateKnowsCycleHangarId: (cycleHangarIdDetails: BinaryOptionsType) => void;
  cycleHangarId: string;
  updateCycleHangarId: (cycleHangarId: string) => void;
  knowsLockCode: BinaryOptionsType;
  updateKnowsLockCode: (LockCodeDetails: BinaryOptionsType) => void;
  lockCode: string;
  updateLockCode: (lockCode: string) => void;
  selectedDate: SelectedDate;
  updateSelectedDate: (selectedDate: SelectedDate) => void;
  tellUsMoreText: string;
  updateTellUsMoreText: (tellUsMoreText: string) => void;
  vandalismTellUsMoreText: string;
  updateVandalismTellUsMoreText: (vandalismTellUsMoreText: string) => void;
  abandondedTellUsMoreText: string;
  updateAbandondedTellUsMoreText: (abandondedTellUsMoreText: string) => void;
  describeBikeText: string;
  updateDescribeBikeText: (describeBikeText: string) => void;
  cleaningTellUsMoreText: string;
  updateCleaningTellUsMoreText: (cleaningTellUsMoreText: string) => void;
}

export const emptyServiceLocationDetails = {
  address_line_1: '',
  address_line_2: '',
  country: '',
  county: '',
  easting: null,
  latitude: null,
  longitude: null,
  northing: null,
  postcode: '',
  town_city: '',
  UPRN: '',
  USRN: '',
};

export const createIssueDetailsSlice: StateCreator<
  IssueDetailsSlice,
  [],
  [],
  IssueDetailsSlice
> = (set, get) => ({
  cycleHangarIssue: null,
  updateCycleHangarIssue: (cycleHangarIssue) =>
    set(() => ({ cycleHangarIssue })),
  cycleHangarIssueOptions: null,
  confirmation: undefined,
  uploadedImages: [],
  uploadedImagesFileName: [],
  uploadedImagesFileType: [],
  uploadedImagesBlob: [],
  fileDetailsObjects: [],
  isSecured: null,
  updateIsSecured: (isSecured) => set(() => ({ isSecured })),
  doesCodeMatch: '',
  doesKeyTurn: '',
  updateDoesCodeMatch: (doesCodeMatch) => set(() => ({ doesCodeMatch })),
  updateDoesKeyTurn: (doesKeyTurn) => set(() => ({ doesKeyTurn })),
  updateConfirmation: (confirmation: CaseIDProps | undefined) =>
    set(() => ({ confirmation })),
  receiveConfirmationEmail: '',
  updateReceiveConfirmationEmail: (receiveConfirmationEmail) =>
    set(() => ({ receiveConfirmationEmail })),
  caseReferenceId: '',
  slaText: '',
  fileWithLoadedCoordinates: '',
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) =>
    set(() => ({ fileWithLoadedCoordinates })),
  serviceLocationDetails: emptyServiceLocationDetails,
  updateServiceLocationDetails: (serviceLocationDetails) =>
    set(() => ({ serviceLocationDetails })),
  serviceAssetDetails: null,
  updateServiceAssetDetails: (serviceAssetDetails) =>
    set(() => ({ serviceAssetDetails })),
  updateCaseReferenceId: (caseReferenceId) => set(() => ({ caseReferenceId })),
  updateSlaText: (slaText) => set(() => ({ slaText })),
  wantConfirmationEmail: '',
  resetConfirmationInformation: () =>
    set(() => ({
      slaText: '',
      caseReferenceId: '',
      wantConfirmationEmail: '',
      receiveConfirmationEmail: '',
      confirmation: undefined,
    })),
  updateUploadedImagesBlobs: (uploadedImagesBlob) =>
    set(() => ({ uploadedImagesBlob })),
  // File type is not serializable in zustand, so we need to convert the File objects to data URLs
  updateUploadedImages: (uploadedFileParts: PartitionFileObjectsType) => {
    const {
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
      geoDataArrays,
    } = uploadedFileParts;
    const fileDetailsObjects = uploadedImagesFileName.map(
      (filename, index) => ({
        name: filename,
        path: uploadedImagesBlob[index].blob_uri,
      }),
    );
    const { serviceLocationDetails, updateFileWithLoadedCoordinates } = get();
    // update the service location details with the first geo data found
    if (
      !serviceLocationDetails.latitude &&
      !serviceLocationDetails.longitude &&
      geoDataArrays.length > 0
    ) {
      geoDataArrays.some((geoData) => {
        if (
          geoData.latitude !== null &&
          geoData.longitude !== null &&
          geoData.fileName !== null
        ) {
          updateFileWithLoadedCoordinates(geoData.fileName);
          set((state) => ({
            serviceLocationDetails: {
              ...state.serviceLocationDetails,
              latitude: geoData.latitude,
              longitude: geoData.longitude,
            },
          }));
          // break the loop if the first geo data is found
          return true;
        }
        // continue the loop
        return false;
      });
    }
    // update the service location details with the first geo data found
    set({ uploadedImages });
    set({ uploadedImagesFileName });
    set({ uploadedImagesFileType });
    set({ uploadedImagesBlob });
    set({ fileDetailsObjects });
  },
  isPhotoLocationStored: false,
  updateIsPhotoLocationStored: (isPhotoLocationStored) =>
    set(() => ({ isPhotoLocationStored })),
  photoCoordinates: null,
  updatePhotoCoordinates: (photoCoordinates) =>
    set(() => ({ photoCoordinates })),
  knowsCycleHangarId: '',
  cycleHangarId: '',
  updateCycleHangarId: (cycleHangarId) => set(() => ({ cycleHangarId })),
  tellUsMoreText: '',
  updateTellUsMoreText: (tellUsMoreText) => set(() => ({ tellUsMoreText })),
  vandalismTellUsMoreText: '',
  updateVandalismTellUsMoreText: (vandalismTellUsMoreText) =>
    set(() => ({ vandalismTellUsMoreText })),
  abandondedTellUsMoreText: '',
  updateAbandondedTellUsMoreText: (abandondedTellUsMoreText) =>
    set(() => ({ abandondedTellUsMoreText })),
  describeBikeText: '',
  updateDescribeBikeText: (describeBikeText) =>
    set(() => ({ describeBikeText })),
  cleaningTellUsMoreText: '',
  updateCleaningTellUsMoreText: (cleaningTellUsMoreText) =>
    set(() => ({ cleaningTellUsMoreText })),
  updateKnowsCycleHangarId: (knowsCycleHangarId) =>
    set(() => ({ knowsCycleHangarId })),
  knowsLockCode: '',
  updateKnowsLockCode: (knowsLockCode) => set(() => ({ knowsLockCode })),
  lockCode: '',
  updateLockCode: (lockCode) => set(() => ({ lockCode })),
  selectedDate: clearSelectedDate,
  updateSelectedDate: (selectedDate) => set(() => ({ selectedDate })),
  resetIssueDetails: () =>
    set(() => ({
      knowsCycleHangarId: '',
      cycleHangarId: '',
      cycleHangarIssue: null,
      cycleHangarIssueOptions: [],
      uploadedImages: [],
      uploadedImagesFileName: [],
      uploadedImagesFileType: [],
      uploadedImagesBlob: [],
      fileDetailsObjects: [],
      serviceAssetDetails: null,
      isPhotoLocationStored: false,
      serviceLocationDetails: emptyServiceLocationDetails,
      isSecured: '',
      doesCodeMatch: '',
      doesKeyTurn: '',
      knowsLockCode: '',
      lockCode: '',
      vandalismTellUsMoreText: '',
      cleaningTellUsMoreText: '',
      abandondedTellUsMoreText: '',
      describeBikeText: '',
      tellUsMoreText: '',
      selectedDate: clearSelectedDate,
      fileWithLoadedCoordinates: '',
    })),
  resetIssueDetailsInput: () =>
    set(() => ({
      isSecured: '',
      doesCodeMatch: '',
      doesKeyTurn: '',
      knowsLockCode: '',
      lockCode: '',
      vandalismTellUsMoreText: '',
      cleaningTellUsMoreText: '',
      abandondedTellUsMoreText: '',
      describeBikeText: '',
      tellUsMoreText: '',
      selectedDate: clearSelectedDate,
    })),
});
